import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import MansionIcon from "assets/icons/ic_map_type_apartment.svg";
import { StatusNameEnum } from "common/enums/StatusEnum";
import { calcOld } from "common/functions/estates";
import { MinimalEstate } from "common/queries/minimalEstates";
import { Estate } from "graphql/API";
import { isEmpty } from "lodash";
import React, { useCallback, useState } from "react";
import styles from "../PricingDialog.module.scss";
import { dspLayout, layoutForCompare } from "./HouseTable";
import {
  dspCompanyName,
  dspEstatePrice,
  dspSellingDays,
  dspStatus,
  dspUpdatedAt,
  sellingDaysForCompare,
  updateAtForCompare
} from "./LandTable";
import { SortableColumn } from "./SortableColumn";

const dspSquareArea = (estate: Estate) =>
  (estate?.squareArea || 0) - ((estate?.squareArea || 0) % 5);

type ColumnName =
  | "status"
  | "fullAddress"
  | "companyName"
  | "estatePrice"
  | "floor"
  | "squareArea"
  | "layout"
  | "updatedAt"
  | "sellingDays";

const IndividualMansionTable = React.memo(
  (props: { allMansionEstates: Estate[]; condominiumId: string }) => {
    const { allMansionEstates, condominiumId } = props;

    const [sortColumn, setSortColumn] = useState<ColumnName | undefined>(
      undefined
    );
    const [sortDirection, setSortDirection] = useState<
      "asc" | "desc" | undefined
    >(undefined);

    const onSortClick = useCallback(
      (column: ColumnName, sortDirection: "asc" | "desc") => {
        setSortColumn(column);
        setSortDirection(sortDirection);
      },
      []
    );

    const estates = allMansionEstates
      .filter((estate) => estate.condominiumId === condominiumId)
      .toSorted(
        (a, b) =>
          (b.status === StatusNameEnum.公開 ||
          b.status === StatusNameEnum.非公開
            ? 0
            : 1) -
            (a.status === StatusNameEnum.公開 ||
            a.status === StatusNameEnum.非公開
              ? 0
              : 1) || (b.recNo ?? 0) - (a.recNo ?? 0)
      );

    const firstEstateWithName = estates
      .filter((x) => isEmpty(x.condominiumName) === false)
      .at(0);

    const firstEstateWithCompletionMonth = estates
      .filter((x) => (x.completionMonth ?? 0) !== 0)
      .at(0);

    const mansionName =
      firstEstateWithName?.condominiumName || "マンション名不明";

    const old = calcOld(firstEstateWithCompletionMonth as MinimalEstate);

    const sortedEstates = estates.toSorted((a, b) => {
      if (sortColumn === "status") {
        return (
          (dspStatus(a.status) ?? "").localeCompare(dspStatus(b.status) ?? "") *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "fullAddress") {
        return (
          (a.fullAddress ?? "").localeCompare(b.fullAddress ?? "") *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "companyName") {
        return (
          (dspCompanyName(a) ?? "").localeCompare(dspCompanyName(b) ?? "") *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "estatePrice") {
        return (
          (dspEstatePrice(a) - dspEstatePrice(b)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "floor") {
        return (
          ((a.floor ?? 0) - (b.floor ?? 0)) * (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "squareArea") {
        return (
          (dspSquareArea(a) - dspSquareArea(b)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "layout") {
        return (
          (layoutForCompare(a) - layoutForCompare(b)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "updatedAt") {
        return (
          updateAtForCompare(a).localeCompare(updateAtForCompare(b)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      if (sortColumn === "sellingDays") {
        return (
          ((sellingDaysForCompare(a) ?? 0) - (sellingDaysForCompare(b) ?? 0)) *
          (sortDirection === "asc" ? 1 : -1)
        );
      }

      return 0;
    });

    return (
      <Accordion className={styles.accordion}>
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon className={styles.icon} />}
          aria-controls="mansion-content"
          id="mansion-header"
        >
          <Box className={`${styles.category} ${styles.small}`}>
            <Box
              component="img"
              src={MansionIcon}
              alt="マンション"
              className={styles.estateTypeIcon}
            />
            {mansionName}（{old === null ? "築年数不明" : `築 ${old} 年`}）：
            {sortedEstates.length} 件
          </Box>
        </AccordionSummary>
        <AccordionDetails className={styles.details}>
          {sortedEstates.length > 0 ? (
            <TableContainer className={styles.tableContainer}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <SortableColumn
                        column="status"
                        label="ステータス"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell>所在地</TableCell>
                    <TableCell>
                      <SortableColumn
                        column="companyName"
                        label="業者名"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell className={styles.alignRight}>
                      <SortableColumn
                        column="estatePrice"
                        label="価格"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell className={styles.alignRight}>
                      <SortableColumn
                        column="floor"
                        label="所在階"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell className={styles.alignRight}>
                      <SortableColumn
                        column="squareArea"
                        label="専有面積"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell>
                      <SortableColumn
                        column="layout"
                        label="間取り"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell>
                      <SortableColumn
                        column="updatedAt"
                        label="掲載終了"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                    <TableCell className={styles.alignRight}>
                      <SortableColumn
                        column="sellingDays"
                        label="売出日数"
                        currentSortColumn={sortColumn}
                        currentSortDirection={sortDirection}
                        onSortClick={onSortClick}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedEstates.map((estate) => {
                    return (
                      <TableRow key={estate.id}>
                        <TableCell>
                          <Chip
                            label={dspStatus(estate.status)}
                            className={
                              dspStatus(estate.status) === "売出中"
                                ? styles.publicStatusLabel
                                : styles.soldStatusLabel
                            }
                          />
                        </TableCell>
                        <TableCell>{estate.fullAddress}</TableCell>
                        <TableCell>{dspCompanyName(estate)}</TableCell>
                        <TableCell className={styles.alignRight}>
                          {String(dspEstatePrice(estate))} 万
                        </TableCell>
                        <TableCell className={styles.alignRight}>
                          {estate.floor} 階
                        </TableCell>
                        <TableCell className={styles.alignRight}>
                          {String(dspSquareArea(estate))} ㎡
                        </TableCell>
                        <TableCell className={styles.layoutCell}>
                          {dspLayout(estate)}
                        </TableCell>
                        <TableCell>{dspUpdatedAt(estate)}</TableCell>
                        <TableCell className={styles.alignRight}>
                          {dspSellingDays(estate)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box className={styles.noData}>※ 過去事例がありませんでした。</Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }
);

export const MansionTable = React.memo(
  (props: { allMansionEstates: Estate[] }) => {
    const condominiumIds = Array.from(
      new Set(
        props.allMansionEstates
          .map((estate) => estate.condominiumId)
          .filter((x) => x)
      )
    ).toSorted((a, b) => {
      const numberA = Number(a?.substring(2) ?? "x");
      const numberB = Number(b?.substring(2) ?? "x");

      if (!isNaN(numberA) && !isNaN(numberB)) {
        return numberA - numberB;
      }

      return String(a).localeCompare(String(b));
    }) as string[];

    if (condominiumIds.length === 0) {
      return (
        <Accordion className={styles.accordion}>
          <AccordionSummary
            expandIcon={<ArrowDropDownIcon className={styles.icon} />}
            aria-controls="mansion-content"
            id="mansion-header"
          >
            <Box className={`${styles.category} ${styles.small}`}>
              <Box
                component="img"
                src={MansionIcon}
                alt="マンション"
                className={styles.estateTypeIcon}
              />
              マンションの過去事例：{props.allMansionEstates.length} 件
            </Box>
          </AccordionSummary>
          <AccordionDetails className={styles.details}>
            <Box className={styles.noData}>※ 過去事例がありませんでした。</Box>
          </AccordionDetails>
        </Accordion>
      );
    }

    return condominiumIds.map((condominiumId) => (
      <IndividualMansionTable
        key={condominiumId}
        condominiumId={condominiumId}
        allMansionEstates={props.allMansionEstates}
      />
    ));
  }
);
